/**
 * useChatMessages
 *
 * Custom hook for efficiently managing chat messages with virtualization
 * and pagination support for large conversations.
 *
 * Primary responsibilities:
 * - Manage chat message loading and pagination
 * - Handle virtualization for large message sets
 * - Provide optimization for large chats (20k+ tokens)
 * - Track message metrics and statistics
 *
 * Dependencies:
 * - Jotai for state management
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { chatMessagesAtom, selectedChatIdAtom, currentChatMessagesAtom } from '@/atoms/chatAtoms'
import { ChatMessage } from '@/types'
import { Tables } from '@/supabase/types'

interface UseChatMessagesOptions {
  pageSize?: number
  initialPage?: number
  chatId?: string
}

interface UseChatMessagesResult {
  messages: ChatMessage[]
  visibleMessages: ChatMessage[]
  messageCount: number
  isLargeChat: boolean
  messageTokenEstimate: number
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
  goToNextPage: () => void
  goToPreviousPage: () => void
  goToPage: (page: number) => void
  addMessage: (message: ChatMessage) => void
  updateMessage: (messageId: string, updates: Partial<Tables<'messages'>>) => void
  deleteMessage: (messageId: string) => void
  resetMessages: (messages: ChatMessage[]) => void
}

// Estimate tokens based on content length (4 chars ≈ 1 token)
const estimateTokens = (content: string): number => {
  return Math.ceil(content.length / 4)
}

// Estimate message tokens
const estimateMessageTokens = (message: ChatMessage): number => {
  return estimateTokens(message.message.content)
}

/**
 * Custom hook for efficiently managing chat messages with virtualization
 * and pagination support for large conversations.
 */
export const useChatMessages = ({
  pageSize = 50,
  initialPage = 0,
  chatId
}: UseChatMessagesOptions = {}): UseChatMessagesResult => {
  // Get the current chat ID from Jotai state or props
  const selectedChatId = useAtomValue(selectedChatIdAtom)
  const activeChatId = chatId || selectedChatId

  // To handle both writeable and derived atoms, use a conditional approach
  const [allMessages, setAllMessagesInternal] = activeChatId
    ? useAtom(chatMessagesAtom(activeChatId))
    : useAtom(currentChatMessagesAtom)

  // Safely wrapped setter function
  const setAllMessages = useCallback(
    (newMessages: ChatMessage[]) => {
      setAllMessagesInternal(newMessages)
    },
    [setAllMessagesInternal]
  )

  // State for pagination
  const [currentPage, setCurrentPage] = useState(initialPage)

  // Calculate pagination metrics
  const totalPages = useMemo(
    () => Math.ceil(allMessages.length / pageSize),
    [allMessages.length, pageSize]
  )

  // Check if chat is large (> 100 messages or estimated > 20k tokens)
  const isLargeChat = useMemo(() => {
    // Temporarily disable virtualization to fix preview functionality
    return false

    // const messageCountThreshold = 100
    // const tokenThreshold = 20000

    // if (allMessages.length > messageCountThreshold) return true

    // // Estimate total tokens
    // const totalTokens = allMessages.reduce(
    //   (total, message) => total + estimateMessageTokens(message),
    //   0
    // )

    // return totalTokens > tokenThreshold
  }, [allMessages])

  // Calculate total tokens estimate
  const messageTokenEstimate = useMemo(
    () => allMessages.reduce((total, message) => total + estimateMessageTokens(message), 0),
    [allMessages]
  )

  // Get visible messages for current page
  const visibleMessages = useMemo(() => {
    if (!isLargeChat) return allMessages

    const start = currentPage * pageSize
    const end = start + pageSize
    return allMessages.slice(start, end)
  }, [allMessages, currentPage, pageSize, isLargeChat])

  // Pagination controls
  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1)
    }
  }, [currentPage, totalPages])

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1)
    }
  }, [currentPage])

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 0 && page < totalPages) {
        setCurrentPage(page)
      }
    },
    [totalPages]
  )

  // Auto-switch to last page when new messages are added
  useEffect(() => {
    if (isLargeChat && allMessages.length > 0) {
      const lastPage = Math.max(0, totalPages - 1)
      setCurrentPage(lastPage)
    }
  }, [allMessages.length, totalPages, isLargeChat])

  // Message manipulation functions
  const addMessage = useCallback(
    (message: ChatMessage) => {
      setAllMessages([...allMessages, message])
    },
    [allMessages, setAllMessages]
  )

  const updateMessage = useCallback(
    (messageId: string, updates: Partial<Tables<'messages'>>) => {
      const newMessages = allMessages.map((msg) =>
        msg.message.id === messageId ? { ...msg, message: { ...msg.message, ...updates } } : msg
      )
      setAllMessages(newMessages)
    },
    [allMessages, setAllMessages]
  )

  const deleteMessage = useCallback(
    (messageId: string) => {
      const newMessages = allMessages.filter((msg) => msg.message.id !== messageId)
      setAllMessages(newMessages)
    },
    [allMessages, setAllMessages]
  )

  const resetMessages = useCallback(
    (messages: ChatMessage[]) => {
      setAllMessages(messages)
    },
    [setAllMessages]
  )

  return {
    messages: allMessages,
    visibleMessages,
    messageCount: allMessages.length,
    isLargeChat,
    messageTokenEstimate,
    currentPage,
    totalPages,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
    goToNextPage,
    goToPreviousPage,
    goToPage,
    addMessage,
    updateMessage,
    deleteMessage,
    resetMessages
  }
}
